import { Logger } from "../../../../packages/logger/src";
import { getLocalStorageItem, setLocalStorageItem } from "../../../logger/src";
export const VALUES = '_values';
export const KEY = 'key';
export const ENTITLEMENT_BASIC = 'bc-sp-basic';
export const ENTITLEMENT_STANDARD = 'bc-sp-standard';
export const ENTITLEMENT_BROADWORKS_CONN = 'broadworks-connector';
export const NATIVE_SIP_CALL_TO_UCM = 'NATIVE_SIP_CALL_TO_UCM';
export const NATIVE_WEBEX_TEAMS_CALLING = 'NATIVE_WEBEX_TEAMS_CALLING';
export const FEATURES_KEY_TYPE_DEVELOPER = "developer";
export const FEATURE_TOGGLE_BROADWORKS_VM_ENABLED = "broadworks-voicemail-enabled-spark-541886";
export const FEATURE_TOGGLE_MSTEAMS_DIALPAD_WEBEX_CONTACTS = "msteams-dialpad-webex-contacts-spark-420840";
export const dispatchStorageEvent = () => window.dispatchEvent(new Event("storage"));

export const setLocalStoragewithDispatchEvent = (key: string, value: any) => {
    setLocalStorageItem(key, value);
    dispatchStorageEvent();
};

export type Model = {
    _values: {
        key: string;
    };
};

export enum CALLING_BACKEND {
    WXC = 'WEBEX_CALLING',
    BWRKS = 'BROADWORKS_CALLING',
    UCM = 'UCM_CALLING',
    INVALID = 'Calling backend is currently not supported',
}


export const VM_BROADWORKS_RESPONSE = {
    "statusCode": 300,
    "data": {
        "error": "Voicemail feature not supported for Broadworks user"
    },
    "message": "FAILURE"
}

export const CALLING_BACKEND_NOT_IDENTIFIED_RESPONSE = {
    "statusCode": 302,
    "data": {
        "error": "Calling backend is not identified, exiting...."
    },
    "message": "FAILURE"
}

const CALLING_BACKEND_STORAGE_KEY = "webex_userCallingBackend";

const resolveCallingBackend = (): CALLING_BACKEND | null => {
    const callingBehavior = window?.webex?.internal?.device?.callingBehavior;
    const entModels: Model[] | undefined = window?.webex?.internal?.device?.features?.entitlement?.models;

    if (!callingBehavior || !Array.isArray(entModels) || entModels.length === 0) {
        // Device not registered or entitlements not yet populated
        return null;
    }

    if (callingBehavior === NATIVE_WEBEX_TEAMS_CALLING) {
        for (let i = 0; i < entModels.length; i += 1) {
            const value = entModels[i][VALUES][KEY];
            if (value === ENTITLEMENT_BASIC || value === ENTITLEMENT_STANDARD) {
                return CALLING_BACKEND.WXC;
            }
            if (value === ENTITLEMENT_BROADWORKS_CONN) {
                return CALLING_BACKEND.BWRKS;
            }
        }
        return CALLING_BACKEND.INVALID;
    }

    if (callingBehavior === NATIVE_SIP_CALL_TO_UCM) {
        return CALLING_BACKEND.UCM;
    }

    return CALLING_BACKEND.INVALID;
};

export function getCallingBackEnd(): CALLING_BACKEND {
    const detected = resolveCallingBackend();

    if (!detected) {
        const cached = getLocalStorageItem<CALLING_BACKEND>(CALLING_BACKEND_STORAGE_KEY);
        Logger.info(`Calling Behaviour not ready; using cached backend: ${cached ?? CALLING_BACKEND.INVALID}`);
        return cached ?? CALLING_BACKEND.INVALID;
    }

    Logger.info(`Calling Behaviour is ${detected}`);
    setLocalStorageItem(CALLING_BACKEND_STORAGE_KEY, detected);
    return detected;
}

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Wait until entitlements/callingBehavior are populated so the backend
 * can be identified. Throws after timeout to avoid infinite waits.
 */
export async function waitForCallingBackEndReady(
    timeoutMs = 15000,
    pollMs = 500
): Promise<CALLING_BACKEND> {
    const start = Date.now();

    while (true) {
        const backend = resolveCallingBackend();
        if (backend && backend !== CALLING_BACKEND.INVALID) {
            Logger.info(`Calling Behaviour resolved after wait: ${backend}`);
            setLocalStorageItem(CALLING_BACKEND_STORAGE_KEY, backend);
            return backend;
        }

        if (Date.now() - start > timeoutMs) {
            throw new Error('waitForCallingBackEndReady: timeout waiting for calling backend');
        }

        await delay(pollMs);
    }
}

 /*
    * Check if feature toggle is enabled/disabled: broadworks-voicemail-enabled-spark-541886
    * Uses partner aggregation to ensure customer org users inherit this toggle from their partner.
    * Returns true if FT is enabled, false if not
 */
export async function setVoicemailForBroadworks(): Promise<boolean> {
    let isBroadworksVoicemailTabEnabled = false;

    if (window?.isWebexInitalized) {
        try {
            // First attempt: Use cached feature from device registration
            isBroadworksVoicemailTabEnabled = await window.webex.internal.feature.getFeature(
                FEATURES_KEY_TYPE_DEVELOPER,
                FEATURE_TOGGLE_BROADWORKS_VM_ENABLED
            );

            // Second attempt: If cached value is false/null, make direct API call with partner aggregation
            // This ensures customer org users inherit the toggle from their partner org
            if (!isBroadworksVoicemailTabEnabled) {
                const userId = window.webex.internal.device.userId;

                if (userId) {
                    Logger.info(`Fetching ${FEATURE_TOGGLE_BROADWORKS_VM_ENABLED} with partner aggregation for user: ${userId}`);

                    const response = await window.webex.request({
                        method: 'GET',
                        service: 'feature',
                        resource: `features/users/${userId}/${FEATURES_KEY_TYPE_DEVELOPER}/${FEATURE_TOGGLE_BROADWORKS_VM_ENABLED}`,
                        qs: {
                            partnerAggregationAllowed: true
                        }
                    });

                    // Parse the feature value from response
                    if (response?.body) {
                        const featureValue = response.body.val || response.body.value;
                        isBroadworksVoicemailTabEnabled = featureValue === 'true' || featureValue === true;

                        Logger.info(`Partner aggregation response for ${FEATURE_TOGGLE_BROADWORKS_VM_ENABLED}: ${isBroadworksVoicemailTabEnabled}`);
                    }
                }
            } else {
                Logger.info(`Cached feature ${FEATURE_TOGGLE_BROADWORKS_VM_ENABLED}: ${isBroadworksVoicemailTabEnabled}`);
            }
        } catch (error) {
            Logger.error(`Error fetching Broadworks voicemail feature toggle: ${error}`);
            // Fall through to return false on error
        }
    }

    return !!isBroadworksVoicemailTabEnabled;
}
